import Image from 'next/image';
import React, { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import { Carousel, CarouselProps } from 'react-responsive-carousel';
import styled from 'styled-components';

import LeftArrow from './left-arrow';
import RightArrow from './right-arrow';
import SmallImages from './small-images';

const CarosuelWrapper = styled.div`
  .carousel .thumbs-wrapper {
    margin: 0px;
  }

  .carousel .control-prev.control-arrow:before {
    display: none;
  }

  [dir='rtl'] .control-prev:before {
    display: none;
  }

  .carousel.carousel-slider .control-arrow:hover {
    background: rgba(0, 0, 0, 0);
    opacity: 1;
  }
  .carousel.carousel-slider .control-arrow {
    opacity: 1;
  }

  .carousel .control-next.control-arrow:before {
    display: none;
    opacity: 0;
  }

  [dir='rtl'] .control-next:before {
    display: none;
  }
`;

export interface ImageSliderProps {
  images?: { url: string }[];
  showThumbs?: boolean;
  autoPlay?: boolean;
  width?: number;
  height?: number;
  smallImageClassName?: string;
  imageFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  showSmallImages?: boolean;
  isImagesUnOptimized?: boolean;
  arrowSize?: number;
}

const Carosuel = (props: ImageSliderProps & Partial<CarouselProps>) => {
  const { t } = useTranslation();
  const imageHeight = props.height || 200;
  const [images, setImages] = useState(!props.images?.length ? [{ url: '/placeholder.webp' }] : props.images);
  const [smallImages, setSmallImages] = useState([]);
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    setSmallImages(props.images?.slice(0, 5) || []);
    setImages(!props.images?.length ? [{ url: '/placeholder.webp' }] : props.images);
  }, [props.images, props.images.length]);

  const onIndexChanged = useCallback(
    (index) => {
      setSmallImages(
        images.slice(
          Math.max(Math.min(index - 2, images?.length - 5), 0),
          Math.min(Math.max(index + 3, 5), images?.length),
        ),
      );
    },
    [images],
  );

  const id = useId();
  return (
    <CarosuelWrapper>
      <Carousel
        onChange={onIndexChanged}
        showIndicators={false}
        showThumbs={props.showThumbs}
        showStatus={images.length > 1}
        selectedItem={currentImage}
        renderArrowPrev={(onClickHandler) => (
          <LeftArrow size={props.arrowSize} showArrow={images.length > 1} onClickHandler={onClickHandler} />
        )}
        renderArrowNext={(onClickHandler) => (
          <RightArrow size={props.arrowSize} showArrow={images.length > 1} onClickHandler={onClickHandler} />
        )}
        {...props}
      >
        {images?.map((image) => (
          <Image
            style={{
              objectFit: `${props.imageFit || 'cover'}`,
            }}
            alt={t('common.carouselImageAlt')}
            className="rounded-xl bg-white"
            width="100"
            height={imageHeight}
            quality={100}
            src={image.url}
            loading="lazy"
            key={id}
            unoptimized={props.isImagesUnOptimized}
          />
        ))}
      </Carousel>
      <SmallImages
        smallImages={smallImages}
        setCurrentImage={setCurrentImage}
        showSmallImages={props.showSmallImages}
        smallImageClassName={props.smallImageClassName}
      />
    </CarosuelWrapper>
  );
};

export default Carosuel;
